import React, { useState } from 'react';
import { Snackbar } from '@material-ui/core';
import { GraphQLError } from 'graphql';
import { Alert } from '@material-ui/lab';
import {
  LoadingSpinner, Box, Text,
} from '@bighealth/react-limbix-ui';
import { useParams } from 'react-router-dom';

import { ENROLLMENT_TRACKER_RESULT_QUERY } from '@/apollo/queries';
import {
  UPDATE_ENROLLMENT_TRACKER,
} from '@/apollo/mutations';
import Assessment from '@/components/Assessment';
import {
  MutationUpdateEnrollmentTrackerArgs,
  QueryEnrollmentTrackerResultArgs,
} from '@/apollo/types';
import { GraphQLErrorsType, MutationResultType, UseParamsType } from '@/types';
import { useQuery, useMutation } from '@/hooks/apollo';
import { formatDate } from '@/utils/dateUtils';
import { usePermissions } from '@/hooks/redux';

const EnrollmentTrackerForm: React.FC = () => {
  const { studyUid, userUid } = useParams<UseParamsType>();
  const [{ studyPermissions }] = usePermissions();
  const permissions = studyPermissions[studyUid];
  const canEdit = permissions?.canModifyParticipants;

  const [mutationErrors, setMutationErrors] = useState<GraphQLErrorsType>(null);
  const { loading, data, error } = useQuery<QueryEnrollmentTrackerResultArgs>(ENROLLMENT_TRACKER_RESULT_QUERY, {
    variables: {
      studyUid,
      userUid,
    },
  });
  const [updateEnrollmentTrackerMutation] = useMutation<MutationUpdateEnrollmentTrackerArgs>(
    UPDATE_ENROLLMENT_TRACKER,
    {
      refetchQueries: [{
        query: ENROLLMENT_TRACKER_RESULT_QUERY,
        variables: {
          studyUid,
          userUid,
        },
      }],
    },
  );

  if (loading) {
    return <LoadingSpinner />;
  }
  if (error) {
    return (
      <Text>{error.message}</Text>
    );
  }
  const { enrollmentTrackerResult, studyParticipant } = data;

  const handleSubmit = async (assessmentAnswers: string[], changeReason?: string):Promise<MutationResultType> => {
    const result = await updateEnrollmentTrackerMutation({
      variables: {
        studyUid,
        enrollmentTrackerResultUid: enrollmentTrackerResult.uid,
        assessmentAnswers,
        changeReason,
      },
    });
    if (result.errors && result.errors.length > 0) {
      setMutationErrors(result.errors);
    }
    return result;
  };

  const { assessment, user } = enrollmentTrackerResult;

  return (
    <Box padding="32px">
      <Box display="flex" flexDirection="column" marginBottom="32px">
        <Text as="h2" paddingBottom="16px">
          {`Enrollment Tracker for Participant ${studyParticipant.participantId ?? user.uid}`}
        </Text>
        <Text as="h3" paddingBottom="8px">
          {`Created at: ${formatDate(enrollmentTrackerResult.createdAt)}`}
        </Text>
        <Text as="h3">
          {`Updated at: ${formatDate(enrollmentTrackerResult.updatedAt)}`}
        </Text>
      </Box>
      <Assessment
        onSubmit={handleSubmit}
        assessment={assessment}
        assessmentResult={enrollmentTrackerResult}
        canEditAfterCompletion={canEdit}
        editButtonText="Edit"
        canSkipQuestions
      />
      <Snackbar
        open={!!mutationErrors}
        onClose={() => { setMutationErrors(null); }}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="error">
          {mutationErrors && mutationErrors.map((e:GraphQLError) => e.message)}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EnrollmentTrackerForm;
